import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Layout,
  Modal,
  Form,
  Typography,
  Skeleton,
  Upload,
  Radio,
  message,
  Button,
  Input,
  notification,
  Tooltip,
  Select,
  Menu,
  Dropdown,
  Checkbox,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { difference, isEqual, pick, uniq } from 'lodash';
import CopyToClipboard from 'react-copy-to-clipboard';
import SparkyPlayer from '@devunlimit/compose-frontend-player';
import { UploaderContext } from '@lib/video-uploader';
import { ReactComponent as IconEditorChapter } from '@svg/icon-editor-chapter-2.svg';
import { ReactComponent as IconUpload } from '@svg/common/icon-upload.svg';
import { ReactComponent as IconInfo } from '@svg/icon-info-dark.svg';
import { ReactComponent as IconCopy } from '@svg/icon-copy.svg';
import { ReactComponent as IconCheckWhite } from '@svg/common/icon-done-white.svg';
import { ReactComponent as IconMore } from '@svg/icon-more.svg';
import { ReactComponent as IconHelp } from '@svg/common/icon-help.svg';
import { ReactComponent as IconThumbnailMore } from '@svg/icon-thumbnail-more.svg';

import { ClientUploadedThumbnail, IVideoDataEditableThumbnails } from '@interfaces/video.interface';
import { scVarTransitionDuration } from '@utils/emotion/variables';
import axios from 'axios';
import api from '@lib/api';
import {
  scMixinEllipsis,
  scMixinPseudoBlock,
  scMixinAbsoluteFull,
  scMixinScreenReaderOnly,
} from '@utils/emotion/mixins';
import { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';

import Title from 'antd/lib/typography/Title';
import TextArea from 'antd/lib/input/TextArea';
import {
  formatDigitToHHmmss,
  getBase64FromImage,
  getImageDimension,
  getViewerUrlByStage,
  getCategoryIdByName,
  getCategoryNameById,
  getHighestQualityVideoItem,
  getThumbnailWithQuality,
  getSelectedThumbnailItem,
} from '~/utils';
import { CategoriesForExplanation, CategoriesForPose } from '~/config/category';
import { useUIStore } from '@stores/ui';
import useVideoContext from '@lib/hooks/useVideoContext';

const { Content: AntdContent } = Layout;
const { Dragger } = Upload;

enum VideoLifecycleStatus {
  ERROR = 0,
  UPLOAD_FAILED = 1,
  UPLOAD_PROGRESS = 10,
  TRANSCODING_PROGRESS = 20,
  ESTIMATION_PROGRESS = 30,
  NORMAL = 40,
}

type FrequentlyUsedTags = {
  [key: string]: string[];
};

const frequentlyUsedTags: FrequentlyUsedTags = {
  ko: [
    '유산소',
    '웨이트',
    '코어',
    '크로스핏',
    '타바타',
    '줌바',
    'Kpop',
    '댄스',
    'BTS',
    '맨몸',
    '운동',
    '스쿼트',
    '플랭크',
    '스트레칭',
    '팔뚝살',
    '런지',
  ],
  en: [
    'Cardio',
    'Weight',
    'ABS',
    'Crossfit',
    'Tabata',
    'Zumba',
    'Kpop',
    'Dance',
    'BTS',
    'No Equipment',
    'Squat',
    'Plank',
    'Stretching',
    'Arm fat',
    'lunge',
  ],
};

const RESTRICTED_TAGS_STARTSWITH = [
  '!',
  '@',
  '$',
  '%',
  '^',
  '&',
  '*',
  '(',
  ')',
  '-',
  '=',
  '+',
  '[',
  ']',
  '{',
  '}',
  '/',
  '\\',
  ',',
  '.',
  '?',
  ';',
  ':',
  "'",
  '"',
  '`',
  '~',
];

type MatchParams = {
  videoId: string;
};

const VideoEdit: React.FC = observer(() => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { videoId } = useParams<keyof MatchParams>();
  const { showGlobalLoadingDimmer, hideGlobalLoadingDimmer } = useUIStore();
  const typeFormFieldLabelRef = useRef<HTMLElement>(null);
  const [{ data, loading, error }, fetch] = useVideoContext(videoId);
  const [isPreviewPlaying, setPreviewPlaying] = useState(false);
  const [isModified, setModified] = useState(false);
  const [isValidForm, setValidForm] = useState(true);
  const [onSubmitting, setSubmitStatus] = useState(false);
  const [thumbnails, setThumbnails] = useState<
    (IVideoDataEditableThumbnails | ClientUploadedThumbnail)[]
  >([]);
  const [selectedThumbnailId, setSelectedThumbnailId] = useState<string | undefined>();
  const [onUploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [form] = Form.useForm();
  const [isCheckedGame, setCheckedGame] = useState<boolean | null>(null);
  const [isSelectedCategory, setSelectedCategory] = useState<boolean | null>(null);
  const [formTags, setFormTags] = useState<string[]>([]);
  const videoUploader = useContext(UploaderContext);
  const displayFrequentlyUsedTags = useMemo(
    () => frequentlyUsedTags[i18n.languages[0]].sort(() => 0.5 - Math.random()).slice(0, 5),
    [i18n.languages],
  );

  const validateMessages = {
    required: t('error.form.validation.required'),
  };

  const currentVideoStatus = useMemo<VideoLifecycleStatus>(() => {
    if (!data || (!data && error)) return VideoLifecycleStatus.ERROR;

    const { jobs, duration } = data;

    if (jobs.transcoding === 'pending' && jobs.poseEstimation === 'pending') {
      const uid = videoUploader.getUidByVideoId(videoId!);

      if (uid) {
        switch (videoUploader.videos[uid].state) {
          case 'cancelling':
          case 'error':
            return VideoLifecycleStatus.UPLOAD_FAILED;
          default:
            return VideoLifecycleStatus.UPLOAD_PROGRESS;
        }
      }

      return VideoLifecycleStatus.UPLOAD_FAILED;
    } else if (jobs.transcoding === 'processing' || !duration) {
      return VideoLifecycleStatus.TRANSCODING_PROGRESS;
    } else if (jobs.poseEstimation === 'processing') {
      return VideoLifecycleStatus.ESTIMATION_PROGRESS;
    } else if (!duration) {
      return VideoLifecycleStatus.ERROR;
    }

    return VideoLifecycleStatus.NORMAL;
  }, [videoId, data, error, videoUploader]);

  useEffect(() => {
    return () => {
      // notification.close('instruction_explanation');
      notification.close('instruction_pose');
    };
  }, []);

  // BeforeUnload Event Handler
  useEffect(() => {
    const message = t('video.editor.alert.back');

    function handler(e: BeforeUnloadEvent) {
      if (isModified) e.returnValue = message;

      return '';
    }

    if (isModified) {
      window.onbeforeunload = handler;
      window.onunload = handler;
    } else {
      window.onbeforeunload = null;
      window.onunload = null;
    }
    // const unblock = history.block(() => {
    //   if (isModified) return message;

    //   return undefined;
    // });

    return () => {
      // unblock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModified, i18n.languages]);

  useEffect(() => {
    if (!loading && error) {
      Modal.error({
        title: t('error.api.common.retrieve.server.title'),
        content: t('error.api.edit.failed'),
        okText: t('error.back'),
        onOk() {
          navigate(-1);
        },
      });

      return;
    }

    if (data && !loading && !error) {
      const { title, description, type, categories, tags, jobs } = data;

      if (currentVideoStatus === VideoLifecycleStatus.TRANSCODING_PROGRESS) {
        message.loading(t('video.edit.message.processing'), 4);
      }

      if (jobs.poseEstimation === 'processing') {
        message.loading(t('video.edit.message.processing'), 4);
      }

      // if (data.type === 'explanation') {
      //   notification.info({
      //     key: 'instruction_explanation',
      //     icon: <IconInfo width="22" height="22" />,
      //     message: t('video.edit.notification.explanation.message'),
      //     description: t('video.edit.notification.explanation.description'),
      //     style: {
      //       width: 500,
      //       marginTop: 56,
      //     },
      //     btn: (
      //       <Button
      //         type="primary"
      //         size="large"
      //         icon={
      //           <IconEditorChapter
      //             width="20"
      //             height="20"
      //             style={{ marginTop: 2, marginRight: 8, verticalAlign: 'top' }}
      //           />
      //         }
      //         style={{ backgroundColor: '#52C41A', borderColor: '#52C41A', color: '#090909' }}
      //         onClick={() => navigate(`/video/${videoId}/visibility`)}
      //       >
      //         {t('video.edit.notification.explanation.button')}
      //       </Button>
      //     ),
      //     duration: 0,
      //   });
      // }

      if (
        data.type === 'pose' &&
        data.categories[0] !== undefined &&
        jobs.poseEstimation === 'pending' &&
        currentVideoStatus > VideoLifecycleStatus.TRANSCODING_PROGRESS
      ) {
        notification.info({
          key: 'instruction_pose',
          icon: <IconInfo width="22" height="22" />,
          message: t('video.edit.notification.pose.message'),
          description: t('video.edit.notification.pose.description'),
          style: {
            width: 500,
            marginTop: 56,
          },
          btn: (
            <Button
              type="primary"
              size="large"
              icon={
                <IconEditorChapter
                  width="20"
                  height="20"
                  style={{ marginTop: 2, marginRight: 8, verticalAlign: 'top' }}
                />
              }
              style={{ backgroundColor: '#52C41A', borderColor: '#52C41A', color: '#090909' }}
              onClick={() => navigate(`/video/${videoId}/editor/chapter`)}
            >
              {t('video.edit.notification.pose.button')}
            </Button>
          ),
          duration: 0,
        });
      }

      setThumbnails(data.thumbnails);
      const selectedThumbnail = data.thumbnails.find((t) => t.isSelected);
      setSelectedThumbnailId(selectedThumbnail?.thumbnailId);

      form.setFieldsValue({
        title,
        description,
        type: type === 'pose',
        tags,
        category: getCategoryIdByName(categories[0]),
        thumbnailId: selectedThumbnail?.thumbnailId,
      });
      setCheckedGame(type === 'pose');
      setFormTags(tags);
      setSelectedCategory(categories[0] !== undefined);
    }
  }, [data, loading, error, navigate, form, videoId, currentVideoStatus, t]);

  const isCustomThumbnailNeverUploaded = useMemo(
    () => thumbnails.length === 1 && 'isGenerated' in thumbnails[0],
    [thumbnails],
  );

  const onPreviewPlay = () => {
    setPreviewPlaying(true);
  };

  const startUploadThumbnail = async (options: RcCustomRequestOptions) => {
    const { file } = options;

    if (!(file instanceof File)) return;

    if (file.size > 10 * (1024 * 1000)) {
      // Limit Up to 10MB
      return Modal.error({
        title: t('video.edit.form.thumbnail.modal.error.size.title'),
        content: t('video.edit.form.thumbnail.modal.error.size.content'),
        okText: t('a11y.ok'),
      });
    }

    const hideUploadingMessage = message.loading(t('video.edit.form.thumbnail.uploading'), 0);
    setUploadingThumbnail(true);

    try {
      const imageDimension = await getImageDimension(file);

      if (imageDimension.width > 1920 || imageDimension.height > 1080) {
        throw new Error('Too large dimension');
      }
      const imageBase64 = await getBase64FromImage(file);

      const { data } = await api.put<{ thumbnailId: string }>(`/video/${videoId}/thumbnail`, [
        {
          extension: file.type.split('/')[1],
          data: imageBase64.split(',')[1],
        },
      ]);

      if (isCustomThumbnailNeverUploaded) {
        setThumbnails([
          { isClient: true, isSelected: true, thumbnailId: data.thumbnailId, url: imageBase64 },
        ]);
        setSelectedThumbnailId(data.thumbnailId);
      } else {
        setThumbnails((prevState) => [
          ...prevState,
          { isClient: true, isSelected: false, thumbnailId: data.thumbnailId, url: imageBase64 },
        ]);
      }

      setUploadingThumbnail(false);
      hideUploadingMessage();
      message.success(t('video.edit.form.thumbnail.uploading.complete'), 3);
    } catch (e) {
      setUploadingThumbnail(false);
      hideUploadingMessage();

      if (axios.isAxiosError(e)) {
        if (e.isAxiosError) {
          return Modal.error({
            title: t('video.edit.form.thumbnail.modal.error.upload.title'),
            content: t('video.edit.form.thumbnail.modal.error.upload.content'),
            okText: t('a11y.ok'),
          });
        }
      }

      if (e instanceof Error) {
        switch (e.message) {
          case 'Too large dimension':
            Modal.error({
              title: t('video.edit.form.thumbnail.modal.error.dimension.title'),
              content: t('video.edit.form.thumbnail.modal.error.dimension.content'),
              okText: t('a11y.ok'),
            });
            break;
          default:
            Modal.error({
              title: t('error.api.common.submit.server.title'),
              content: t('error.api.common.submit.server.content'),
              okText: t('a11y.ok'),
            });
        }
      }
    }
  };

  const removeThumbnail = (id: string) => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: t('video.edit.form.thumbnail.remove.confirm.title'),
      content: t('video.edit.form.thumbnail.remove.confirm.content'),
      okText: t('common.delete'),
      cancelText: t('a11y.cancel'),
      async onOk() {
        try {
          let isTryingToDeleteSelectedThumbnail = false;

          if (selectedThumbnailId === id) {
            if (thumbnails.length > 1) {
              const index = thumbnails.findIndex((thumbnail) => thumbnail.thumbnailId === id);

              if (index < 0) return;
              isTryingToDeleteSelectedThumbnail = true;
              const nextSelectedThumbnailId =
                thumbnails[index + 1 < thumbnails.length ? index + 1 : index - 1].thumbnailId;

              await api.patch(`/video/${videoId}`, {
                thumbnailId: nextSelectedThumbnailId,
              });
              setSelectedThumbnailId(nextSelectedThumbnailId);
            } else {
              return;
            }
          }
          if (
            !isTryingToDeleteSelectedThumbnail &&
            getSelectedThumbnailItem(data?.thumbnails)?.thumbnailId === id
          ) {
            await api.patch(`/video/${videoId}`, {
              thumbnailId: selectedThumbnailId,
            });
          }
          await api.delete('/thumbnail/' + id);
          setThumbnails(thumbnails.filter((t) => t.thumbnailId !== id));
          message.success(t('video.edit.form.thumbnail.delete.complete'), 4);
        } catch (e) {
          message.error(t('video.edit.form.thumbnail.delete.error'), 4);
        }
      },
      okType: 'danger',
      onCancel() {
        // console.log('Cancel');
      },
    });
  };

  const onFieldsChange = (changeFields: any[], allFields: any[]) => {
    if (changeFields.length < 1) return;
    const initialSelectedThumbnailId =
      data?.thumbnails.find((t) => t.isSelected)?.thumbnailId ?? '';
    const isGameCheckedField = allFields.find((f: any) => f.name[0] === 'type');
    const isChangedGame = changeFields.find((f: any) => f.name[0] === 'type') !== undefined;
    const isSelectedCategoryField = changeFields.find((f: any) => f.name[0] === 'category');
    const isGameChecked = isGameCheckedField.value;

    if (!isSelectedCategory && isSelectedCategoryField?.value) {
      setSelectedCategory(true);
    }

    if (isChangedGame) {
      form.setFieldsValue({
        category: Object.keys(
          isGameChecked ? CategoriesForPose : CategoriesForExplanation,
        )[0].toString(),
      });
    }

    setCheckedGame(isGameChecked);
    setModified(
      !isEqual(
        [
          ...Object.values(pick(data, ['title', 'type', 'categories', 'tags', 'description'])),
          initialSelectedThumbnailId,
        ],
        [
          ...allFields.map((field) => {
            if (field.name.includes('type')) {
              return field.value ? 'pose' : 'explanation';
            }

            if (field.name.includes('category')) {
              return field.value ? [getCategoryNameById(parseInt(field.value))] : undefined;
            }

            return field.value;
          }),
          selectedThumbnailId,
        ],
      ),
    );
    setValidForm(
      allFields
        .filter((field) => field.name[0] !== 'description')
        .map((field) => {
          if (field.name.includes('type')) {
            return field.value ? 'pose' : 'explanation';
          }

          if (field.name.includes('category')) {
            if (currentVideoStatus <= VideoLifecycleStatus.TRANSCODING_PROGRESS) {
              return true;
            }

            return field.value !== undefined;
          }

          return field.value;
        })
        .every((value) => {
          if (typeof value === 'boolean') return value;
          if (typeof value === 'string') {
            return value.length > 0;
          } else {
            return true;
          }
        }),
    );
  };

  const handleTagsInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    let value = e.currentTarget.value;
    if (e.key === 'Space') return;
    if (value.startsWith(' ')) value = value.trimStart();
    if (e.key === 'Enter') {
      e.preventDefault();
      value = value.trimEnd();
    }

    e.currentTarget.value = value.replace(/ +(?= )/g, '');
  };

  const addTag = (tag: string) => {
    if (formTags.some((t) => t === tag)) return;

    setFormTags((prevState) => [...prevState, tag]);
    setModified(true);
  };

  const handleTagsChange = (value: any) => {
    const parsedValue = (value as string[]).reduce<string[]>((acc, curr) => {
      if (!curr || RESTRICTED_TAGS_STARTSWITH.some((t) => curr.startsWith(t))) return acc;
      if (curr.startsWith('#') && curr.indexOf(' #') > 1) {
        const occuredTags = curr.match(/#\S+/g);

        if (occuredTags) acc.push(...occuredTags);
        else acc.push(curr);

        return acc;
      }

      acc.push(curr);

      return acc;
    }, []);

    setFormTags(uniq(parsedValue));

    if (data?.tags) {
      if (difference(data.tags, value).length || difference(value, data.tags).length) {
        setModified(true);
      }
    }
  };

  const onSelectThumbnail = (id: string) => {
    setSelectedThumbnailId(id);
    form.setFieldsValue({
      thumbnailId: id,
    });
    form.validateFields();
  };

  const submit = (values: any) => {
    // Validation
    if (values.type === 'pose' && (data?.duration ?? 0) < 30) return;
    if (currentVideoStatus > VideoLifecycleStatus.TRANSCODING_PROGRESS && !values.category) {
      return;
    }
    showGlobalLoadingDimmer();
    // notification.close('instruction_explanation');
    notification.close('instruction_pose');
    setSubmitStatus(true);

    api
      .patch(`/video/${videoId}`, {
        ...values,
        description: values.description?.trim() ?? '',
        type: values.type ? 'pose' : 'explanation',
        tags: formTags,
        category: values.category ? [values.category] : undefined,
        calorieBurn: 0,
        thumbnailId: selectedThumbnailId,
      })
      .then(() => {
        setModified(false);
        message.success(t('video.edit.form.submit.success'), 4);
        window.setTimeout(() => {
          hideGlobalLoadingDimmer();
          fetch();
          setSubmitStatus(false);
        }, 300);
      })
      .catch(() => {
        hideGlobalLoadingDimmer();
        setSubmitStatus(false);
        Modal.error({
          title: t('error.api.common.submit.server.title'),
          content: t('error.api.common.submit.server.content'),
          okText: t('a11y.ok'),
        });
      });
  };

  const deleteVideo = useCallback(async () => {
    Modal.confirm({
      title: t('videos.action.delete.single.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('videos.action.delete.single.description'),
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('a11y.cancel'),
      async onOk() {
        message.success(t('video.edit.message.deleted'), 4);
        const uid = videoUploader.getUidByVideoId(videoId!);
        if (uid) videoUploader.cancelVideoUpload(uid, true);
        await api.delete(`/video/${videoId}`);
        navigate('/videos', { replace: true });
      },
    });
  }, [t, navigate, videoId, videoUploader]);

  const editMoreMenu = useMemo(
    () => (
      <Menu
        mode="vertical"
        items={[
          {
            key: 'delete',
            label: <button>{t('common.delete.long')}</button>,
            onClick: () => deleteVideo(),
          },
        ]}
        style={{ width: 160 }}
      />
    ),
    [t, deleteVideo],
  );

  if (!data || loading || error) {
    return (
      <ContentBlock id="content">
        <div className="header">
          <Typography>
            <Title level={2}>{t('video.edit.title')}</Title>
          </Typography>
        </div>
        <div className="content">
          <Skeleton active />
        </div>
      </ContentBlock>
    );
  }

  return (
    <ContentBlock id="content">
      <div className="header">
        <Typography>
          <Title level={2}>{t('video.edit.title')}</Title>
        </Typography>
      </div>
      {data && !loading && !error ? (
        <div className="content">
          <Form
            form={form}
            name="video_form"
            layout="vertical"
            hideRequiredMark
            validateMessages={validateMessages}
            autoComplete="off"
            onFieldsChange={onFieldsChange}
            onFinish={submit}
          >
            <div className="form-content">
              <div className="form-content-field">
                <Form.Item
                  name="title"
                  label={t('video.edit.form.title')}
                  rules={[
                    {
                      required: true,
                      min: 1,
                      max: 100,
                    },
                  ]}
                >
                  <Input
                    value={data?.title}
                    placeholder={t('video.edit.form.title.placeholder')}
                    maxLength={100}
                  />
                </Form.Item>
                <Form.Item
                  name="type"
                  valuePropName="checked"
                  rules={[{ required: true }]}
                  hidden={currentVideoStatus <= VideoLifecycleStatus.TRANSCODING_PROGRESS}
                >
                  <div className="custom-form-item">
                    <strong ref={typeFormFieldLabelRef} className="custom-form-label">
                      {t('video.edit.form.commentaryonly.title')}
                    </strong>
                    <Tooltip
                      getPopupContainer={() => typeFormFieldLabelRef.current!}
                      title={t('video.edit.form.commentaryonly.tooltip2')}
                      placement="bottomLeft"
                      visible={currentVideoStatus === VideoLifecycleStatus.ESTIMATION_PROGRESS}
                    />
                    <Tooltip
                      getPopupContainer={() => typeFormFieldLabelRef.current!}
                      title={t('video.edit.form.commentaryonly.tooltip3')}
                      placement="bottomLeft"
                      visible={data.published && !data.pose?.chapters}
                    />
                    <Tooltip
                      getPopupContainer={() => typeFormFieldLabelRef.current!}
                      title={t('video.edit.form.commentaryonly.tooltip4')}
                      placement="bottomLeft"
                      visible={currentVideoStatus <= VideoLifecycleStatus.TRANSCODING_PROGRESS}
                    />
                    <Tooltip
                      getPopupContainer={() => typeFormFieldLabelRef.current!}
                      title={t('video.edit.form.commentaryonly.tooltip5')}
                      placement="bottomLeft"
                      visible={
                        currentVideoStatus > VideoLifecycleStatus.ESTIMATION_PROGRESS &&
                        data.duration < 30
                      }
                    />
                    <span className="custom-form-desc">
                      {t('video.edit.form.commentaryonly.desc')}
                    </span>
                    <div className="custom-type">
                      <div className="custom-form-content">
                        <div className="custom-form-box">
                          <Checkbox
                            defaultChecked={data.type === 'pose'}
                            disabled={
                              currentVideoStatus <= VideoLifecycleStatus.ESTIMATION_PROGRESS ||
                              (data.published && !data.pose?.chapters) ||
                              data?.duration < 30
                            }
                          >
                            {t('video.edit.form.commentaryonly.checkbox.label')}
                          </Checkbox>
                        </div>
                      </div>
                    </div>
                  </div>
                </Form.Item>
                <Form.Item
                  name="category"
                  rules={[
                    {
                      required: currentVideoStatus > VideoLifecycleStatus.TRANSCODING_PROGRESS,
                    },
                  ]}
                  hidden={currentVideoStatus <= VideoLifecycleStatus.TRANSCODING_PROGRESS}
                >
                  <Radio.Group>
                    <div className="custom-form-item">
                      <strong className="custom-form-label">
                        {t('video.edit.form.category.title')}
                      </strong>
                      <span className="custom-form-desc">{t('video.edit.form.category.desc')}</span>
                      {!isSelectedCategory && (
                        <span className="custom-form-validation-error">
                          {t('error.form.validation.category')}
                        </span>
                      )}
                      <div className="custom-category">
                        <div
                          className="custom-category-type"
                          aria-disabled={isCheckedGame ? 'false' : 'true'}
                        >
                          <strong className="custom-category-subtitle">
                            <span className="subtitle-text">
                              {t('video.edit.form.category.pose.label')}
                            </span>
                            <Tooltip
                              placement="right"
                              title={t('video.edit.form.category.explanation.type1.tip')}
                              overlayStyle={{
                                zIndex: 10,
                                maxWidth: 'none',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <i className="icon-tip" aria-label="Tip">
                                <IconHelp />
                              </i>
                            </Tooltip>
                          </strong>
                          <div className="custom-category-box">
                            <div className="custom-category-row">
                              <Radio value="1" disabled={!isCheckedGame ?? false}>
                                <i className="icon-check">
                                  <IconCheckWhite width="13" height="10" />
                                </i>
                                {t('video.category.fitness')}
                              </Radio>
                              <Radio value="2" disabled={!isCheckedGame ?? false}>
                                <i className="icon-check">
                                  <IconCheckWhite width="13" height="10" />
                                </i>
                                {t('video.category.dance')}
                              </Radio>
                            </div>
                          </div>
                        </div>
                        <div
                          className="custom-category-type"
                          aria-disabled={isCheckedGame ? 'true' : 'false'}
                        >
                          <strong className="custom-category-subtitle">
                            <span className="subtitle-text">
                              {t('video.edit.form.category.explanation.label')}
                            </span>
                            <Tooltip
                              placement="right"
                              title={t('video.edit.form.category.explanation.type2.tip')}
                              overlayStyle={{
                                zIndex: 10,
                                maxWidth: 'none',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <i className="icon-tip" aria-label="Tip">
                                <IconHelp />
                              </i>
                            </Tooltip>
                          </strong>
                          <div className="custom-category-box">
                            <div className="custom-category-row">
                              <span className="custom-category-row-desc">
                                {t('video.edit.form.category.explanation.type1.label')}
                              </span>
                              <Radio value="4" disabled={isCheckedGame ?? false}>
                                <i className="icon-check">
                                  <IconCheckWhite width="13" height="10" />
                                </i>
                                {t('video.category.knowledge')}
                              </Radio>
                            </div>
                            <div className="custom-category-row">
                              <span className="custom-category-row-desc">
                                {t('video.edit.form.category.explanation.type2.label')}
                              </span>
                              <Radio value="5" disabled={isCheckedGame ?? false}>
                                <i className="icon-check">
                                  <IconCheckWhite width="13" height="10" />
                                </i>
                                {t('video.category.life')}
                              </Radio>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Radio.Group>
                </Form.Item>
                <Form.Item name="tags">
                  <div className="custom-form-item">
                    <label htmlFor="form_tags" className="custom-form-label">
                      {t('video.edit.form.tags')}
                    </label>
                    <div className="custom-form-desc">{t('video.edit.form.tags.description')}</div>
                    <div className="custom-tags">
                      <div className="custom-form-content">
                        <div className="custom-tag-content">
                          <div className="custom-tag-row">
                            <div className="custom-tag-box">
                              <strong className="custom-tag-category">
                                {t('video.edit.form.tags.box.frequentlyused')}
                              </strong>
                              <div className="custom-tag-list">
                                {displayFrequentlyUsedTags.map((tag) => (
                                  <button
                                    key={tag}
                                    type="button"
                                    className="btn-add-tag"
                                    onClick={() => addTag(tag)}
                                  >
                                    {tag}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <TagsInput
                        mode="tags"
                        value={formTags}
                        id="form_tags"
                        open={false}
                        notFoundContent={false}
                        allowClear={true}
                        tokenSeparators={[',']}
                        placeholder={t('video.edit.form.tags.placeholder')}
                        maxTagCount={10}
                        maxTagTextLength={20}
                        maxTagPlaceholder={(values) =>
                          t('video.edit.form.tags.more', { count: values.length })
                        }
                        onInputKeyDown={handleTagsInputKeyDown}
                        onChange={handleTagsChange}
                      />
                    </div>
                  </div>
                </Form.Item>
                <Form.Item
                  name="description"
                  label={t('video.edit.form.description')}
                  rules={[
                    {
                      min: 1,
                      max: 5000,
                    },
                  ]}
                >
                  <TextArea
                    value={data?.description}
                    placeholder={t('video.edit.form.description.placeholder')}
                    autoSize={{ minRows: 10, maxRows: 46 }}
                    maxLength={5000}
                  />
                </Form.Item>
                <div className="custom-form-item">
                  <strong className="custom-form-label">{t('video.edit.form.thumbnail')}</strong>
                  <span className="custom-form-desc">
                    {thumbnails.some((thumbnail) => 'isGenerated' in thumbnail)
                      ? t('video.edit.form.thumbnail.desc.v1')
                      : t('video.edit.form.thumbnail.desc.v2')}
                  </span>
                  <div className="custom-form-content">
                    <div className="custom-thumbnail-area">
                      {thumbnails.map((thumbnail, index) => (
                        <div
                          key={thumbnail.thumbnailId ?? 'is-generated'}
                          className="thumbnail-item"
                          data-id={thumbnail.thumbnailId ?? 'is-generated'}
                          aria-selected={
                            selectedThumbnailId == thumbnail.thumbnailId ? 'true' : 'false'
                          }
                          role={thumbnails.length > 1 ? 'button' : undefined}
                          onClick={() => {
                            if (!thumbnail.thumbnailId) return;

                            onSelectThumbnail(thumbnail.thumbnailId);
                          }}
                        >
                          {thumbnails.length === 1 &&
                          thumbnail.isSelected &&
                          !('isGenerated' in thumbnail) ? (
                            <></>
                          ) : (
                            <Dropdown
                              overlay={
                                isCustomThumbnailNeverUploaded ? (
                                  <Menu
                                    mode="vertical"
                                    items={[
                                      {
                                        key: 'delete',
                                        label: (
                                          <Upload
                                            accept="image/jpeg,image/png"
                                            multiple={false}
                                            showUploadList={false}
                                            customRequest={startUploadThumbnail}
                                            className="ant-upload-block"
                                            style={{
                                              position: 'relative',
                                              padding: 0,
                                            }}
                                          >
                                            {t('video.edit.form.thumbnail.more.change')}
                                          </Upload>
                                        ),
                                        onClick: (info) => {
                                          info.domEvent.preventDefault();
                                          info.domEvent.stopPropagation();
                                        },
                                      },
                                    ]}
                                    style={{ width: 160 }}
                                  />
                                ) : (
                                  <Menu
                                    mode="vertical"
                                    items={[
                                      {
                                        key: 'delete',
                                        label: t('video.edit.form.thumbnail.more.delete'),
                                        disabled:
                                          thumbnail.thumbnailId === selectedThumbnailId &&
                                          thumbnails.length === 1 &&
                                          index === 0,
                                        onClick: (info) => {
                                          info.domEvent.stopPropagation();
                                          if (!thumbnail.thumbnailId) return;

                                          removeThumbnail(thumbnail.thumbnailId);
                                        },
                                      },
                                    ]}
                                    style={{ width: 160 }}
                                  />
                                )
                              }
                              trigger={['click']}
                              placement="bottom"
                            >
                              <Button
                                type="text"
                                size="large"
                                icon={<IconThumbnailMore />}
                                className="thumbnail-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <span className="text">{t('common.more')}</span>
                              </Button>
                            </Dropdown>
                          )}
                          <div
                            className="thumbnail-image"
                            style={{
                              backgroundImage: `url(${
                                'isClient' in thumbnail
                                  ? thumbnail.url
                                  : getThumbnailWithQuality(thumbnail, 'medium') ??
                                    '/assets/player/default-video.png'
                              })`,
                            }}
                          />
                          <div className="thumbnail-hover-message">
                            {t('video.edit.form.thumbnail.hover.message')}
                          </div>
                        </div>
                      ))}
                      {!thumbnails.length && (
                        <div className="custom-thumbnail-skeleton">
                          <p>{t('video.edit.form.thumbnail.skeleton')}</p>
                        </div>
                      )}
                      {!isCustomThumbnailNeverUploaded && thumbnails.length < 4 && (
                        <Dragger
                          name="custom-thumbnail"
                          className="custom-thumbnail-dragger"
                          accept="image/jpeg,image/png"
                          multiple={false}
                          showUploadList={false}
                          disabled={!thumbnails.length}
                          customRequest={startUploadThumbnail}
                        >
                          <i className="icon">
                            <IconUpload />
                          </i>
                          <span className="text">
                            {t('video.edit.form.thumbnail.dragger.desc')}
                          </span>
                        </Dragger>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-content-video">
                <div className="video-summary">
                  {currentVideoStatus <= VideoLifecycleStatus.TRANSCODING_PROGRESS && (
                    <div className="transcoding-progress-dimmer">
                      <video muted autoPlay loop playsInline controls={false}>
                        <source src="/assets/player/processing.webm" type="video/webm" />
                      </video>
                    </div>
                  )}
                  <div className="video-inner">
                    {currentVideoStatus > VideoLifecycleStatus.TRANSCODING_PROGRESS ? (
                      <SparkyPlayer
                        video={{
                          src: getHighestQualityVideoItem(data.transcodings)!,
                          // thumbnails: data.thumbnails,
                        }}
                        options={{
                          autoplay: false,
                        }}
                        onLoaded={() => onPreviewPlay()}
                      />
                    ) : (
                      <div className="default-screen" />
                    )}
                    <div className="duration-box" aria-hidden={isPreviewPlaying ? 'true' : 'false'}>
                      {!!data.duration && (
                        <span className="duration-value">
                          {formatDigitToHHmmss(data.duration, true, data.duration >= 3600, true)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="video-info">
                    <dl className="info-list">
                      {data.published && (
                        <div className="info-item type-url">
                          <dt className="info-label">{t('video.edit.info.videourl')}</dt>
                          <dd className="info-value">
                            <a
                              href={`${getViewerUrlByStage()}/watch?v=${videoId}`}
                              className="url-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {`${getViewerUrlByStage()}/watch?v=${videoId}`}
                            </a>
                            <div className="btn-copy">
                              <CopyToClipboard
                                text={`${getViewerUrlByStage()}/watch?v=${videoId}`}
                                onCopy={() =>
                                  message.success(t('video.edit.info.videourl.oncopied'))
                                }
                              >
                                <Tooltip placement="bottom" title={t('common.copy')}>
                                  <div role="button" aria-label={t('common.copy')}>
                                    <IconCopy width="24" height="24" />
                                  </div>
                                </Tooltip>
                              </CopyToClipboard>
                            </div>
                          </dd>
                        </div>
                      )}
                      <div className="info-item">
                        <dt className="info-label">{t('video.edit.form.title')}</dt>
                        <dd className="info-value">{data.title}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
                {currentVideoStatus === VideoLifecycleStatus.UPLOAD_PROGRESS && (
                  <p className="transcoding-progress-message">
                    {t('video.edit.preview.default.uploading')}
                  </p>
                )}
                {currentVideoStatus === VideoLifecycleStatus.TRANSCODING_PROGRESS && (
                  <p className="transcoding-progress-message">
                    {t('video.edit.preview.default.processing')}
                  </p>
                )}
              </div>
            </div>
            <div className="form-action">
              <Button
                htmlType="submit"
                type="primary"
                size="large"
                style={{ width: '160px' }}
                loading={onSubmitting}
                disabled={!isModified || !isValidForm || onUploadingThumbnail}
              >
                {t('a11y.submit')}
              </Button>
              <Dropdown overlay={editMoreMenu} trigger={['click']} placement="topRight">
                <Button
                  type="text"
                  size="large"
                  icon={<IconMore width="24" height="24" />}
                  className="btn-more"
                >
                  <span className="text">{t('common.more')}</span>
                </Button>
              </Dropdown>
            </div>
          </Form>
        </div>
      ) : (
        <div className="content">
          <Skeleton active />
        </div>
      )}
    </ContentBlock>
  );
});

const shineLine = keyframes`
0% {
  background-position: -100px;
}

100% {
  background-position: 480px;
}
`;

const ContentBlock = styled(AntdContent)`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 19px 36px 44px;

  .header {
  }

  .content {
    flex: 1 1 auto;
    padding: 32px 35px;
    background-color: ${({ theme }) => theme.colors.secondary.bgColor};
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);

    .ant-form {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .form-content {
      flex: 1 1 auto;
      display: flex;

      .ant-form-item {
        & + .ant-form-item,
        & + .custom-form-item {
          margin-top: 60px;
        }

        & + .custom-form-desc {
          margin-top: -20px;
        }
      }

      .ant-form-item-label {
        margin-top: -8px;
        padding: 8px 0;

        label {
          font-weight: bold;
          font-size: 15px;
          line-height: 1.5;
          color: ${({ theme }) => theme.colors.fontColor};
          transition: color ${scVarTransitionDuration} ease;
        }
      }
    }

    .form-content-field {
      flex: 1 1 auto;
      overflow: hidden;
      margin: 0 30px 0 -6px;
      padding-left: 6px;
      box-sizing: border-box;
    }

    .form-content-video {
      flex: 0 0 352px;
      width: 352px;
      margin: 30px 0 0;

      .video-summary {
        position: relative;
      }

      .transcoding-progress-dimmer {
        ${scMixinAbsoluteFull};
        overflow: hidden;
        z-index: 1;
        border-radius: 3px;
        background-color: rgba(0, 0, 0, 0.8);

        video {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
        }
      }

      .transcoding-progress-message {
        margin-top: 12px;
        font-size: 13px;
        line-height: 1.5;
        color: #e0000c;
      }

      .video-inner {
        overflow: hidden;
        position: relative;
        border-radius: 3px 3px 0 0;

        .default-screen {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 160px;
          background-color: #eee;

          i {
            font-style: normal;
          }
        }

        .duration-box {
          position: absolute;
          right: 3px;
          bottom: 3px;
          transition: opacity 100ms ease;

          &[aria-hidden='true'] {
            opacity: 0;
            pointer-events: none;
          }
        }

        .duration-value {
          padding: 0 4px 1px;
          border-radius: 3px;
          background: rgba(9, 9, 9, 0.8);
          font-size: 12px;
          line-height: 1.3;
          color: #f5f6f8;
        }
      }

      .video-info {
        overflow: hidden;
        padding: 15px 12px;
        border-radius: 0 0 3px 3px;
        background-color: #f5f6f8;
      }

      .url-link {
        display: block;
        ${scMixinEllipsis};
        color: #5100ce;
      }

      .info-list {
        margin: 0;
      }

      .info-item {
        position: relative;

        & + .info-item {
          margin-top: 30px;
        }

        &.type-url {
          padding-right: 36px;
        }

        .btn-copy {
          position: absolute;
          top: 6px;
          right: 0;
          cursor: pointer;
        }
      }

      .info-label {
        margin: 0;
        font-size: 14px;
        line-height: 1.5;
        color: ${({ theme }) => theme.colors.fontSubColor};
      }

      .info-value {
        margin: 2px 0 0;
        font-size: 14px;
        line-height: 1.5;
        color: ${({ theme }) => theme.colors.fontColorInverted};
      }

      .side-form {
        margin-top: 14px;

        /* .ant-select-selection-item {
          padding: 4px 0;
          font-size: 14px;
          line-height: 1.3;
          color: #334d6e;
        } */

        /* Antd Select customization */
        /* src/scss/common.scss -> .ant-select-item.ant-select-item-option */
        /* .select-published-icon {
          display: inline-block;
          position: relative;
          top: 5px;
          margin-right: 8px;
        }

        .select-published-text {
          font-size: 14px;
          line-height: 1.3;
        } */
      }
    }

    .form-action {
      margin-top: 64px;
      text-align: right;

      .btn-more {
        position: relative;
        top: 5px;

        .text {
          ${scMixinScreenReaderOnly};
        }
      }
    }
  }

  .custom-form-item {
    position: relative;

    & + .custom-form-item,
    & + .ant-row.ant-form-item {
      margin-top: 60px;
    }

    .custom-form-label {
      display: block;
      margin: 0;
      font-weight: bold;
      font-size: 15px;
      line-height: 1.5;
      color: ${({ theme }) => theme.colors.fontColor};
      transition: color ${scVarTransitionDuration} ease;
      cursor: default;
    }

    .custom-form-desc {
      display: block;
      margin: 4px 0 0;
      font-size: 13px;
      line-height: 1.5;
      color: ${({ theme }) => theme.colors.fontSubColor};
      white-space: pre-line;
    }

    .custom-form-validation-error {
      display: block;
      margin-top: 5px;
      font-size: 13px;
      line-height: 1.2;
      color: #e0000c;
    }

    .custom-form-content {
      margin-top: 16px;

      .explanation-yn-area {
        &[aria-disabled='true'] {
          opacity: 0.3;
        }
      }

      .custom-form-box {
        display: inline-block;
        position: relative;
        min-width: 520px;
        margin: 0;
        padding: 0;
        border: 1px solid #edf1f6;
        border-radius: 3px;
        color: ${({ theme }) => theme.colors.fontSubColor};
      }
    }
  }

  .custom-thumbnail-area {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    margin-top: -5px;
    padding: 5px 0;

    &.thumbnail-item,
    & > span {
      flex: 1;
    }

    .custom-thumbnail-dragger,
    .thumbnail-item,
    .custom-thumbnail-skeleton {
      width: 160px;
      height: 90px;
      vertical-align: top;
    }

    .custom-thumbnail-skeleton {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background-color: #f5f6f8;
      background-image: linear-gradient(135deg, #f5f6f8 50px, #ffffff 80px, #f5f6f8 100px);
      background-size: 800px;
      animation: ${shineLine} 1.6s infinite linear;

      p {
        margin: 10px 30px;
        font-size: 12px;
        line-height: 20px;
        text-align: center;
        color: #959595;
      }
    }
    .custom-thumbnail-dragger {
      position: relative;

      .ant-upload-btn {
        display: block !important;
        margin: 0;
        padding: 0;
      }

      .ant-upload-drag-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
      }

      .icon svg {
        width: 32px;
        height: 32px;

        circle {
        }

        path {
          fill: #fff;
        }
      }

      .text {
        font-size: 12px;
        line-height: 1.5;
        white-space: pre-line;
      }

      &.ant-upload-disabled {
        border: 1px dashed #9d9d9d;

        .icon svg circle {
          fill: #b6b7b8;
        }

        .text {
          color: #959595;
        }
      }
    }

    .thumbnail-item {
      overflow: hidden;
      position: relative;
      border-radius: 3px;
      cursor: pointer;

      &:hover::before {
        opacity: 1;
      }

      &::before {
        ${scMixinPseudoBlock};
        ${scMixinAbsoluteFull};
        z-index: 1;
        background-color: rgba(0, 0, 0, 0.3);
        opacity: 0;
        transition: opacity 200ms ease;
      }

      &[aria-selected='true'] {
        border-radius: 3px;
        outline: 5px solid #5100ce;
      }

      .thumbnail-more {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 2;
        height: auto;
        padding: 0;

        .text {
          ${scMixinScreenReaderOnly};
        }
      }

      .thumbnail-image {
        width: 100%;
        height: 100%;
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;
      }

      .thumbnail-hover-message {
        display: none;
        justify-content: center;
        align-items: center;
        position: absolute;
        inset: 0 0 0 0;
        text-align: center;
        font-size: 15px;
        color: #ffffff;
        z-index: 1;
      }

      &:hover[aria-selected='false'] .thumbnail-hover-message {
        display: flex;
      }
    }
  }

  .custom-tags {
    padding: 0;

    .ant-select {
      margin-top: 20px;

      .ant-select-selector {
        min-height: 42px;
      }

      .ant-select-selection-item {
        height: auto;
        padding: 5px 10px;
        border-radius: 50px;
        border-color: #4700b3;
        background-color: #5100ce;
        color: #f5f6f8;

        svg path {
          fill: #f5f6f8;
        }
      }
    }

    .custom-tag-row {
      display: inline-block;

      & + .custom-tag-row {
        margin-top: 12px;
      }

      &.custom-tag-row--flex-row {
        display: flex;
        flex-direction: row;

        &[data-is-korean='true'] {
          .custom-tag-box + .custom-tag-box {
            margin-left: 14px;
          }
        }

        &[data-is-korean='false'] {
          flex-direction: row-reverse;
          justify-content: flex-end;

          .custom-tag-box + .custom-tag-box {
            margin-right: 14px;
          }
        }
      }
    }

    .custom-tag-category {
      display: block;
      font-size: 12px;
      line-height: 1.2;
      font-weight: bold;
      color: #334d6e;
    }

    .custom-tag-list {
      margin-top: 6px;
      padding: 13px 15px;
      border: 1px solid #edf1f6;
      border-radius: 3px;
      color: ${({ theme }) => theme.colors.fontSubColor};
    }

    .btn-add-tag {
      display: inline-block;
      padding: 5px 10px;
      border-radius: 50px;
      background-color: #ebeff4;
      font-size: 13px;
      line-height: 1.2;
      vertical-align: top;
      color: #090909;
      cursor: pointer;
      user-select: none;

      &:hover {
        background-color: #5100ce;
        color: #f5f6f8;
      }

      & + .btn-add-tag {
        margin-left: 5px;
      }
    }
  }

  .custom-type {
    .custom-form-content .custom-form-box {
      padding: 24px 16px;
    }
  }

  .custom-category {
    .ant-radio-wrapper {
      input,
      .ant-radio {
        ${scMixinScreenReaderOnly};
      }

      .icon-check {
        display: none;
        position: relative;
        top: 6px;
        margin-right: 5px;
        line-height: 0;
        vertical-align: top;
      }

      span:last-child {
        display: inline-block;
        padding: 12px 24px;
        border-radius: 50px;
        font-size: 15px;
        line-height: 1.2;
        color: #000;
        background-color: #ebeff4;
        user-select: none;
      }

      &:not(.ant-radio-wrapper-disabled):hover span:last-child {
        background-color: #5100ce;
        color: #f5f6f8;
      }

      &.ant-radio-wrapper-checked {
        .icon-check {
          display: inline-block;
        }

        span:last-child {
          background-color: #5100ce;
          color: #f5f6f8;
        }
      }
    }
  }

  .custom-category-type {
    margin-top: 12px;

    &[aria-disabled='true'] {
      .custom-category-subtitle .subtitle-text,
      .custom-category-box .custom-category-row,
      .custom-category-box .custom-category-row-desc {
        opacity: 0.2;
      }
    }
  }

  .custom-category-subtitle {
    font-size: 13px;
    line-height: 1.5;
    color: #334d6e;

    .icon-tip {
      display: inline-block;
      margin-left: 6px;
      vertical-align: top;
    }
  }

  .custom-category-box {
    min-width: 520px;
    margin-top: 6px;
    padding: 16px;
    border: 1px solid #edf1f6;
    border-radius: 3px;
    box-sizing: border-box;
  }

  .custom-category-row {
    & + .custom-category-row {
      margin-top: 18px;
    }
  }

  .custom-category-row-desc {
    display: block;
    margin-bottom: 18px;
    font-size: 15px;
    line-height: 1.2;
    color: #000;
  }

  .video-js {
    width: auto;
    height: 100%;

    .vjs-big-play-button {
      top: 50%;
      left: 50%;
      margin: -25px 0 0 -45px;
    }
  }
`;

const TagsInput = styled(Select)`
  width: 100%;
`;

export default VideoEdit;
